import React, { useState, useEffect } from "react";
import Layout from "./AdminLayouts";
import { FiEdit, FiTrash } from "react-icons/fi";
import axios from "axios";
import localforage from "localforage";
import API_BASE_URL from "./apiConfig";

const baseInputStyle = {
  flex: "1",
  padding: "6px",
  borderRadius: "5px",
  border: "1px solid #000",
  fontSize: "12px",
  height: "32px",
  boxSizing: "border-box",
};

const AdminProduct = () => {
  // Form states
  const [productName, setProductName] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [discount, setDiscount] = useState("");
  const [image, setImage] = useState("");
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [openingStock, setOpeningStock] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [pieces, setPieces] = useState("");
  const [ratePer, setRatePer] = useState("");
  const [productStatus, setProductStatus] = useState("Visible");
  
  // Category states
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  
  // Unit states
  const [unitOfSale, setUnitOfSale] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState("");
  
  // Product states
  const [products, setProducts] = useState([]);
  const [editId, setEditId] = useState(null);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [showMediaPopup, setShowMediaPopup] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showProductMediaGallery, setShowProductMediaGallery] = useState(false);
  const [productMediaUrls, setProductMediaUrls] = useState([]);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [showBulkUploadPopup, setShowBulkUploadPopup] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  
  // Search and filter states
  const [searchProductName, setSearchProductName] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  // Fetch units of sale
  useEffect(() => {
    const fetchUnitOfSale = async () => {
      try {
        const token = await localforage.getItem("jwtToken");
        if (!token) return;

        const response = await axios.get(`${API_BASE_URL}api/Crackers/GetUnitOfSale`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data?.statusCode === 200 && Array.isArray(response.data.data)) {
          setUnitOfSale(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching units:", error);
      }
    };

    fetchUnitOfSale();
  }, []);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = await localforage.getItem("jwtToken");
        if (!token) throw new Error("No authentication token found");

        const response = await axios.get(`${API_BASE_URL}api/Crackers/GetCategories`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { CompanyId: 3 },
        });

        // Normalize categories data
        const categoriesData = (response.data?.data || response.data?.result || response.data || [])
          .map(cat => ({
            id: cat.id || cat.categoryId || cat._id,
            name: cat.name || cat.categoryName || cat.title
          }))
          .filter(cat => cat.id && cat.name);

        setCategories(categoriesData);
      } catch (err) {
        console.error("Error fetching categories:", err);
        setError("Failed to load categories");
      }
    };

    fetchCategories();
  }, []);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const token = await localforage.getItem("jwtToken");
        if (!token) throw new Error("No authentication token found");

        const response = await axios.get(`${API_BASE_URL}api/Crackers/GetProductSummary`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { CompanyId: 3 },
        });

        setProducts(response.data?.result || []);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to load products");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Handle form submission
  const handleAddProduct = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const token = await localforage.getItem("jwtToken");
      if (!token) throw new Error("No authentication token found");

      const formData = new FormData();
      formData.append("CategoryId", selectedCategory);
      formData.append("ProdName", productName.trim());
      formData.append("ProdPrice", parseFloat(productPrice));
      formData.append("DiscountPercentage", parseFloat(discount) || 0);
      formData.append("companyId", 3);
      formData.append("OpeningStock", openingStock ? parseInt(openingStock, 10) : 0);
      formData.append("unitID", selectedUnitId);
      formData.append("pieces", pieces);
      formData.append("expiryDate", expiryDate);
      formData.append("ProductStatus", productStatus);

      // When editing, send existing image URLs to preserve them
      if (editId && previewUrl) {
        formData.append("ExistingImageUrls", previewUrl);
      }

      // Append main image (if exists and is a file)
      if (image && typeof image !== "string") {
        formData.append("ProdImage", image);
      } else {
        // Send empty file to satisfy validation
        formData.append("ProdImageUrl", "");
      }

      // Append multiple images
      if (images && images.length > 0) {
        images.forEach((img) => {
          formData.append("ProdImages", img);
        });
      }

      // Append multiple videos or send empty to satisfy validation
      if (videos && videos.length > 0) {
        videos.forEach((video) => {
          formData.append("ProdVideos", video);
        });
      } else {
        // Send empty array indicator
        formData.append("ProdVideos", "");
      }

      if (editId) formData.append("id", editId);
      if (ratePer) formData.append("ratePer", ratePer);

      await axios.post(`${API_BASE_URL}api/Crackers/AddOrUpdateProduct`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // Refresh product list
      const productsResponse = await axios.get(`${API_BASE_URL}api/Crackers/GetProductSummary`, {
        params: { companyId: 3 },
        headers: { Authorization: `Bearer ${token}` },
      });

      setProducts(productsResponse.data?.result || []);
      resetForm();
      alert(`Product ${editId ? "updated" : "added"} successfully!`);
    } catch (error) {
    console.error("Error saving product:", error);

    // ✅ Extract and show API error message
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const statusDesc = error.response.data?.statusDesc;

      if (status === 400 && statusDesc) {
        // Show exact message from API (e.g., duplicate name)
        alert(statusDesc); // 👈 "A product with this name already exists."
      } else if (status === 401) {
        alert("Session expired. Please log in again.");
        window.location.href = "/login";
      } else {
        // Generic fallback
        alert(statusDesc || "Request failed. Please try again.");
      }
    } else if (error.request) {
      // No response (network issue)
      alert("No response from server. Check your internet connection.");
    } else {
      // Other errors (e.g., invalid URL)
      alert(`Error: ${error.message}`);
    }
  } finally {
    setIsSubmitting(false);
  }
};

  const validateForm = () => {
    if (!productName.trim()) {
      alert("Please enter a product name.");
      return false;
    }
    if (!productPrice || isNaN(parseFloat(productPrice))) {
      alert("Please enter a valid product price.");
      return false;
    }
    if (!selectedCategory) {
      alert("Please select a category.");
      return false;
    }
    if (!selectedUnitId) {
      alert("Please select a unit of sale.");
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setProductName("");
    setProductPrice("");
    setDiscount("");
    setImage("");
    setImages([]);
    setVideos([]);
    setOpeningStock("");
    setExpiryDate("");
    setPieces("");
    setEditId(null);
    setSelectedCategory("");
    setSelectedUnitId("");
    setPreviewUrl("");
    setRatePer("");
    setProductStatus("Visible");
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = "";
    const mediaInput = document.getElementById("media-input");
    if (mediaInput) mediaInput.value = "";
  };

  const handleEdit = async (id) => {
    try {
      setIsLoading(true);
      const token = await localforage.getItem("jwtToken");
      if (!token) {
        alert("Session expired. Please login again.");
        window.location.href = "/login";
        return;
      }

      const response = await axios.get(`${API_BASE_URL}api/Crackers/GetProductById`, {
        params: { productId: id },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.statusCode === 200 && response.data.data) {
        const product = response.data.data;
        console.log('Product data from API:', product); // Debug log
        console.log('Product status values:', {
          productStatus: product.productStatus,
          ProductStatus: product.ProductStatus,
          status: product.status,
          Status: product.Status
        }); // Debug log
        
        setProductName(product.prodName || "");
        setProductPrice(product.prodPrice || "");
        setDiscount(product.discountPercentage || "");
        setImage(product.prodImage?.trim() || "");
        setPreviewUrl(product.prodImage?.trim() || "");
        setOpeningStock(product.openingStock || "");
        setPieces(product.pieces || "");
        setExpiryDate(product.expiryDate ? product.expiryDate.split("T")[0] : "");
        setSelectedCategory(product.categoryId?.toString() || "");
        setSelectedUnitId(product.unitID || "");
        
        // Check multiple possible field names for status
        const statusValue = product.productStatus || product.ProductStatus || product.status || product.Status || "Visible";
        console.log('Setting status to:', statusValue); // Debug log
        setProductStatus(statusValue);
        
        // Load existing images if available
        if (product.prodImage && product.prodImage.trim()) {
          // If there's a main image, show it as preview
          setPreviewUrl(product.prodImage.trim());
        }
        
        // Load additional images/videos if available from API
        // Assuming the API returns images array or similar field
        if (product.images && Array.isArray(product.images)) {
          setImages(product.images);
        }
        if (product.videos && Array.isArray(product.videos)) {
          setVideos(product.videos);
        }
        
        setEditId(id);
      }
    } catch (error) {
      console.error("Error loading product:", error);
      alert("Failed to load product. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

const handleDelete = async (id) => {
  if (!window.confirm("Are you sure you want to delete this product?")) return;

  try {
    const token = await localforage.getItem("jwtToken");
    if (!token) {
      alert("Not authenticated. Please log in.");
      window.location.href = "/login";
      return;
    }

    // Make the delete request
    const deleteResponse = await axios.post(
      `${API_BASE_URL}api/Crackers/DeleteProduct`,
      { productId: id },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (deleteResponse.data.statusCode === 200) {
      // Immediately remove the item from local state for better UX
      setProducts(prevProducts => prevProducts.filter(product => product.id !== id));
      
      // Then refresh from server to ensure consistency
      try {
        const productsResponse = await axios.get(
          `${API_BASE_URL}api/Crackers/GetProductSummary`, 
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { CompanyId: 3 } // Make sure to include CompanyId
          }
        );

        // Handle different possible response structures
        const productsData = productsResponse.data?.result || 
                           productsResponse.data?.data || 
                           productsResponse.data || 
                           [];
        
        setProducts(Array.isArray(productsData) ? productsData : []);
      } catch (refreshError) {
        console.error("Error refreshing products:", refreshError);
        // We already updated the UI, so just log the error
      }
      
      alert("Product deleted successfully!");
    } else {
      throw new Error(deleteResponse.data.statusDesc || "Failed to delete product");
    }
  } catch (error) {
    console.error("Delete error:", error);
    alert(error.response?.data?.statusDesc || "Failed to delete product. Please try again.");
    
    // Refresh the list anyway to ensure we're in sync with the server
    try {
      const token = await localforage.getItem("jwtToken");
      if (token) {
        const productsResponse = await axios.get(
          `${API_BASE_URL}api/Crackers/GetProductSummary`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { CompanyId: 3 }
          }
        );
        setProducts(productsResponse.data?.result || []);
      }
    } catch (refreshError) {
      console.error("Error during refresh after failed delete:", refreshError);
    }
  }
};

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 300 * 1024) {
      alert("Image must be smaller than 300KB");
      return;
    }

    setImage(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleMultipleMediaChange = (e) => {
    const files = Array.from(e.target.files || []);
    
    const newImages = [];
    const newVideos = [];
    
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        if (file.size > 500 * 1024) {
          alert(`${file.name} is too large. Maximum image size is 500KB`);
          return;
        }
        newImages.push(file);
      } else if (file.type.startsWith('video/')) {
        if (file.size > 50 * 1024 * 1024) {
          alert(`${file.name} is too large. Maximum video size is 50MB`);
          return;
        }
        newVideos.push(file);
      } else {
        alert(`${file.name} is not a valid image or video file`);
      }
    });

    const totalFiles = images.length + videos.length + newImages.length + newVideos.length;
    if (totalFiles > 10) {
      alert("Maximum 10 files allowed (images + videos combined)");
      return;
    }

    if (newImages.length > 0) {
      setImages(prev => [...prev, ...newImages]);
    }
    if (newVideos.length > 0) {
      setVideos(prev => [...prev, ...newVideos]);
    }

    // Reset the input
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeVideo = (index) => {
    setVideos(prev => prev.filter((_, i) => i !== index));
  };

  // Media Popup Component
  const MediaPopup = () => (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '2rem',
      }}
      onClick={() => {
        setShowMediaPopup(false);
        setSelectedMedia(null);
      }}
    >
      <div 
        style={{
          position: 'relative',
          maxWidth: '600px',
          maxHeight: '90vh',
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '1rem',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => {
            setShowMediaPopup(false);
            setSelectedMedia(null);
          }}
          style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            width: '35px',
            height: '35px',
            borderRadius: '50%',
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            fontSize: '20px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            zIndex: 10000,
          }}
        >
          ×
        </button>

        {selectedMedia?.type === 'image' ? (
          <img
            src={selectedMedia.url}
            alt={selectedMedia.name}
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '80vh',
              objectFit: 'contain',
              borderRadius: '4px',
            }}
          />
        ) : (
          <video
            src={selectedMedia?.url}
            controls
            autoPlay
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '80vh',
              borderRadius: '4px',
            }}
          />
        )}

        <div style={{
          marginTop: '1rem',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: '600',
          color: '#333',
        }}>
          {selectedMedia?.name}
        </div>
      </div>
    </div>
  );

  // Product Media Gallery Popup
  const ProductMediaGallery = () => {
    const currentUrl = productMediaUrls[currentMediaIndex];
    const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(currentUrl);
    const hasMultiple = productMediaUrls.length > 1;

    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '2rem',
        }}
        onClick={() => {
          setShowProductMediaGallery(false);
          setProductMediaUrls([]);
          setCurrentMediaIndex(0);
        }}
      >
        <div 
          style={{
            position: 'relative',
            maxWidth: '800px',
            maxHeight: '70vh',
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1rem',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              setShowProductMediaGallery(false);
              setProductMediaUrls([]);
              setCurrentMediaIndex(0);
            }}
            style={{
              position: 'absolute',
              top: '-10px',
              right: '-10px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              fontSize: '24px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              zIndex: 10000,
            }}
          >
            ×
          </button>

          {hasMultiple && currentMediaIndex > 0 && (
            <button
              onClick={() => setCurrentMediaIndex(prev => prev - 1)}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                border: 'none',
                fontSize: '24px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
            >
              ‹
            </button>
          )}

          {hasMultiple && currentMediaIndex < productMediaUrls.length - 1 && (
            <button
              onClick={() => setCurrentMediaIndex(prev => prev + 1)}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                border: 'none',
                fontSize: '24px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
            >
              ›
            </button>
          )}

          {isVideo ? (
            <video
              src={currentUrl}
              controls
              autoPlay
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '55vh',
                borderRadius: '4px',
              }}
            />
          ) : (
            <img
              src={currentUrl}
              alt={`Media ${currentMediaIndex + 1}`}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '55vh',
                objectFit: 'contain',
                borderRadius: '4px',
              }}
            />
          )}

          {hasMultiple && (
            <div style={{
              position: 'absolute',
              bottom: '60px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '0.4rem 0.8rem',
              borderRadius: '1rem',
              fontSize: '14px',
              fontWeight: '600',
            }}>
              {currentMediaIndex + 1} / {productMediaUrls.length}
            </div>
          )}

          {hasMultiple && (
            <div style={{
              marginTop: '1rem',
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
              padding: '8px 0',
            }}>
              {productMediaUrls.map((url, index) => {
                const thumbIsVideo = /\.(mp4|webm|ogg|mov)$/i.test(url);
                return (
                  <div
                    key={index}
                    onClick={() => setCurrentMediaIndex(index)}
                    style={{
                      width: '60px',
                      height: '60px',
                      cursor: 'pointer',
                      border: index === currentMediaIndex ? '3px solid #532a51ff' : '2px solid #ddd',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    {thumbIsVideo ? (
                      <video
                        src={url}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <img
                        src={url}
                        alt={`Thumb ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Filter categories based on search
  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  // Filter products based on search and category filter
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchProductName === "" || 
      product.prodName?.toLowerCase().includes(searchProductName.toLowerCase()) ||
      product.productId?.toLowerCase().includes(searchProductName.toLowerCase());
    
    const matchesCategory = filterCategory === "" || 
      product.categoryId?.toString() === filterCategory.toString();
    
    return matchesSearch && matchesCategory;
  });

  // Bulk Upload Functions
  const handleBulkFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'text/csv') {
      setBulkFile(file);
    } else {
      alert('Please select a valid CSV file');
      e.target.value = '';
    }
  };

  const downloadTemplate = () => {
    const headers = ['CategoryName', 'ProductName', 'Price', 'Discount', 'OpeningStock', 'Pieces', 'UnitName', 'ProductStatus'];
    
    // Get available unit names for reference
    const availableUnits = unitOfSale.map(u => u.name).join(' / ');
    
    // Create sample data with actual unit name (OpeningStock and Pieces are optional - shown as empty)
    const sampleData = ['Bombs', 'Sample Cracker', '100', '10', '', '', unitOfSale.length > 0 ? unitOfSale[0].name : 'Box', 'Visible'];
    
    // Create CSV content without comment lines to avoid parsing issues
    const csvContent = [
      headers.join(','),
      sampleData.join(','),
      '',
      `# Available Units: ${availableUnits}`,
      '# ProductStatus: Visible or Hold',
      '# CategoryName: Use exact category name from your categories',
      '# OpeningStock and Pieces are optional (can be left empty)'
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      alert('Please select a CSV file');
      return;
    }

    try {
      setIsBulkUploading(true);
      const token = await localforage.getItem("jwtToken");
      if (!token) {
        alert("Session expired. Please login again.");
        return;
      }

      const formData = new FormData();
      formData.append('file', bulkFile);
      formData.append('companyId', 3);

      const response = await axios.post(
        `${API_BASE_URL}api/Crackers/BulkUploadProducts`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.statusCode === 200) {
        const result = response.data.data;
        let message = `Bulk upload completed!\n\n` +
          `✓ Successfully added: ${result.successfullyAdded || 0} products`;

        // Only show errors if there are any
        if (result.errors && result.errors > 0) {
          message += `\n✗ Errors: ${result.errors}`;
        }

        // Only show duplicates if there are any
        if (result.duplicates && result.duplicates > 0) {
          message += `\n⚠ Duplicates skipped: ${result.duplicates}`;
        }

        message += `\nTotal processed: ${result.totalProcessed || 0}`;

        // Only show error details if there are any
        if (result.errorDetails && result.errorDetails.length > 0) {
          message += `\n\nFirst ${result.errorDetails.length} errors:\n` + result.errorDetails.join('\n');
        }

        alert(message);

        // Refresh products list
        const productsResponse = await axios.get(
          `${API_BASE_URL}api/Crackers/GetProductSummary`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { CompanyId: 3 },
          }
        );
        setProducts(productsResponse.data?.result || []);

        // Close popup and reset
        setShowBulkUploadPopup(false);
        setBulkFile(null);
        const fileInput = document.getElementById('bulk-file-input');
        if (fileInput) fileInput.value = '';
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      const errorMessage = error.response?.data?.statusDesc || 
                          error.response?.data?.error || 
                          'Failed to upload products. Please try again.';
      alert(errorMessage);
    } finally {
      setIsBulkUploading(false);
    }
  };

  // Bulk Upload Popup Component
  const BulkUploadPopup = () => (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={() => {
        setShowBulkUploadPopup(false);
        setBulkFile(null);
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          width: '90%',
          maxWidth: '500px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => {
            setShowBulkUploadPopup(false);
            setBulkFile(null);
          }}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6c757d',
            lineHeight: '1',
          }}
        >
          ×
        </button>

        <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
          Bulk Upload Products
        </h3>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
            Upload CSV File:
          </label>
          <input
            type="file"
            accept=".csv"
            onChange={handleBulkFileChange}
            id="bulk-file-input"
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
          {bulkFile && (
            <p style={{ marginTop: '8px', fontSize: '12px', color: '#28a745' }}>
              ✓ Selected: {bulkFile.name}
            </p>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={downloadTemplate}
            style={{
              padding: '8px 16px',
              backgroundColor: '#532a51ff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Download Template
          </button>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => {
              setShowBulkUploadPopup(false);
              setBulkFile(null);
            }}
            style={{
              padding: '8px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleBulkUpload}
            disabled={isBulkUploading}
            style={{
              padding: '8px 20px',
              backgroundColor: isBulkUploading ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isBulkUploading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: isBulkUploading ? 0.7 : 1,
            }}
          >
            {isBulkUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Layout />
      {showMediaPopup && <MediaPopup />}
      {showProductMediaGallery && <ProductMediaGallery />}
      {showBulkUploadPopup && <BulkUploadPopup />}
      <div style={styles.container}>
        <div style={styles.titleContainer}>
          <div style={{ ...styles.filterRow, justifyContent: "right", marginTop: "10px", gap: "10px" }}>
            <button 
              onClick={handleAddProduct} 
              style={{ ...styles.addButton, width: "auto", minWidth: "120px" }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : (editId ? "Update Product" : "Add Product")}
            </button>
            <button 
              onClick={() => setShowBulkUploadPopup(true)} 
              style={{ ...styles.addButton, backgroundColor: "#28a745", width: "auto", minWidth: "120px" }}
            >
              Bulk Upload
            </button>
          </div>
        </div>

        {/* Add/Edit Product Form */}
        <div style={styles.filterContainer}>
          {/* Row 1: Category, Product Name, Units, Pieces */}
          <div style={styles.filterRow}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Category:<span style={{ color: "red" }}>*</span></label>
              <div style={{ position: "relative", flex: 1 }}>
                <div
                  style={{
                    ...baseInputStyle,
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    paddingRight: "25px"
                  }}
                  onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                  onKeyDown={(e) => e.key === "Enter" && setIsCategoryOpen(!isCategoryOpen)}
                  role="combobox"
                  aria-expanded={isCategoryOpen}
                  tabIndex="0"
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", fontSize: "12px" }}>
                    {selectedCategory
                      ? categories.find(c => c.id.toString() === selectedCategory.toString())?.name
                      : "Select Category"}
                  </span>
                  <span style={{ transform: isCategoryOpen ? "rotate(180deg)" : "none" }}>
                    ▼
                  </span>
                </div>

                {isCategoryOpen && (
                  <div style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    maxHeight: "200px",
                    overflowY: "auto",
                    backgroundColor: "#fff",
                    border: "1px solid #000",
                    borderRadius: "5px",
                    marginTop: "2px",
                    zIndex: 1000,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
                  }}>
                    <div style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                      <input
                        type="text"
                        placeholder="Search categories..."
                        style={{
                          width: "100%",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                          fontSize: "12px"
                        }}
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                      />
                    </div>
                    <ul style={{ listStyle: "none", padding: 0, fontSize: "12px", margin: 0 }}>
                      {filteredCategories.length === 0 ? (
                        <li style={{ padding: "10px", color: "#999", fontSize: "12px", textAlign: "center" }}>
                          No matching categories
                        </li>
                      ) : (
                        filteredCategories.map((category) => (
                          <li
                            key={category.id}
                            onClick={() => {
                              setSelectedCategory(category.id.toString());
                              setIsCategoryOpen(false);
                              setCategorySearch("");
                            }}
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              fontSize: "12px",
                              backgroundColor: selectedCategory === category.id.toString()
                                ? "#f0f0f0"
                                : "white",
                              borderBottom: "1px solid #eee",
                              ':hover': {
                                backgroundColor: "#f5f5f5"
                              }
                            }}
                          >
                            {category.name}
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Product Name:<span style={{ color: "red" }}>*</span></label>
              <input
                type="text"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                style={styles.input}
                placeholder="Enter name"
                required
              />
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Units:<span style={{ color: "red" }}>*</span></label>
              <select
                value={selectedUnitId}
                onChange={(e) => setSelectedUnitId(e.target.value)}
                style={styles.select}
                required
              >
                <option value="">Select Unit</option>
                {unitOfSale.map((unit) => (
                  <option key={unit.unitID} value={unit.unitID}>
                    {unit.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Pieces:</label>
              <input
                type="number"
                value={pieces}
                onChange={(e) => setPieces(e.target.value)}
                style={styles.input}
                placeholder="Enter pieces"
                min="0"
                required
              />
            </div>
          </div>

          {/* Row 2: Price, Discount, Opening Stock, Status, Images/Videos */}
          <div style={styles.filterRow}>
            <div style={styles.filterGroup}>
              <label style={{ ...styles.filterLabel, width: '70px' }}>Price (₹):<span style={{ color: "red" }}>*</span></label>
              <input
                type="number"
                value={productPrice}
                onChange={(e) => setProductPrice(e.target.value)}
                style={styles.input}
                placeholder="Price"
                min="0"
                step="0.01"
                required
              />
            </div>

            <div style={styles.filterGroup}>
              <label style={{ ...styles.filterLabel, width: '90px' }}>Discount (%):</label>
              <input
                type="number"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                style={styles.input}
                placeholder="10"
                min="0"
                max="100"
                step="10"
              />
            </div>

            <div style={styles.filterGroup}>
              <label style={{ ...styles.filterLabel, width: '100px' }}>Opening Stock:</label>
              <input
                type="number"
                value={openingStock}
                onChange={(e) => setOpeningStock(e.target.value)}
                style={styles.input}
                placeholder="Stock"
                min="0"
              />
            </div>

            <div style={{ ...styles.filterGroup, flex: '0 0 auto', maxWidth: '180px' }}>
              <label style={{ ...styles.filterLabel, width: '50px' }}>Status:</label>
              <select
                value={productStatus}
                onChange={(e) => setProductStatus(e.target.value)}
                style={{ ...styles.select, maxWidth: '120px' }}
              >
                <option value="Visible">Visible</option>
                <option value="Hold">Hold</option>
              </select>
            </div>

            <div style={styles.filterGroup}>
              <label style={{ ...styles.filterLabel, width: '100px' }}>Images/Videos:</label>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleMultipleMediaChange}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    zIndex: 2,
                  }}
                  id="media-input"
                />
                <div style={{
                  flex: 1,
                  padding: '6px',
                  fontSize: '12px',
                  border: '1px solid #000',
                  borderRadius: '5px',
                  height: '32px',
                  boxSizing: 'border-box',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: (images.length > 0 || videos.length > 0) ? '#532a51ff' : '#666',
                  fontWeight: (images.length > 0 || videos.length > 0) ? '600' : 'normal',
                }}>
                  {(images.length > 0 || videos.length > 0) 
                    ? `${images.length + videos.length} file${images.length + videos.length > 1 ? 's' : ''}`
                    : 'Choose files'}
                </div>
              </div>
            </div>
          </div>

          {/* Existing Images Display (when editing) */}
          {editId && previewUrl && (
            <div style={styles.filterRow}>
              <div style={{ ...styles.filterGroup, flex: '1 1 100%' }}>
                <label style={styles.filterLabel}>Current Images:</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {(() => {
                    // Split comma-separated URLs
                    const imageUrls = previewUrl.split(',').map(url => url.trim()).filter(url => url);
                    
                    return imageUrls.map((imgUrl, index) => {
                      const fullUrl = imgUrl.startsWith("http") ? imgUrl : `${API_BASE_URL}${imgUrl}`;
                      const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(imgUrl);
                      
                      return (
                        <div key={index} style={{ position: 'relative' }}>
                          {isVideo ? (
                            <video
                              src={fullUrl}
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '2px solid #532a51ff',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                setSelectedMedia({
                                  type: 'video',
                                  url: fullUrl,
                                  name: `Product Video ${index + 1}`
                                });
                                setShowMediaPopup(true);
                              }}
                            />
                          ) : (
                            <img
                              src={fullUrl}
                              alt={`Product ${index + 1}`}
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '2px solid #532a51ff',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                setSelectedMedia({
                                  type: 'image',
                                  url: fullUrl,
                                  name: `Product Image ${index + 1}`
                                });
                                setShowMediaPopup(true);
                              }}
                              onError={(e) => {
                                e.target.style.border = '2px solid #ff0000';
                                e.target.alt = 'Failed to load';
                              }}
                            />
                          )}
                          <div style={{
                            position: 'absolute',
                            top: '2px',
                            right: '2px',
                            backgroundColor: '#532a51ff',
                            color: 'white',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: 'bold',
                          }}>
                            {index + 1}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Remove this image?')) {
                                const imageUrls = previewUrl.split(',').map(url => url.trim()).filter(url => url);
                                imageUrls.splice(index, 1);
                                setPreviewUrl(imageUrls.join(','));
                                if (imageUrls.length === 0) {
                                  setImage('');
                                }
                              }
                            }}
                            style={{
                              position: 'absolute',
                              bottom: '2px',
                              right: '2px',
                              backgroundColor: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              width: '26px',
                              height: '26px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '13px',
                              cursor: 'pointer',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            }}
                            title="Remove image"
                          >
                            <FiTrash />
                          </button>
                        </div>
                      );
                    });
                  })()}
                  <span style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>
                    Click images to view full size
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Media Preview Row - Compact */}
          {(images.length > 0 || videos.length > 0) && (
            <div style={styles.filterRow}>
              <div style={{ ...styles.filterGroup, flex: '1 1 100%' }}>
                <label style={styles.filterLabel}>Selected Files:</label>
                <div style={styles.compactFilePreviewContainer}>
                  {/* Display Images */}
                  {images.map((img, index) => (
                    <div key={`img-${index}`} style={styles.miniFilePreviewItem}>
                      <div style={styles.miniMediaTypeBadge}>IMG</div>
                      <img
                        src={URL.createObjectURL(img)}
                        alt={`Preview ${index + 1}`}
                        style={styles.miniPreviewThumbnail}
                        onClick={() => {
                          setSelectedMedia({
                            type: 'image',
                            url: URL.createObjectURL(img),
                            name: img.name
                          });
                          setShowMediaPopup(true);
                        }}
                      />
                      <span style={styles.miniFileName}>{img.name}</span>
                      <span
                        onClick={() => removeImage(index)}
                        style={styles.miniRemoveButton}
                        title="Remove"
                      >
                        ×
                      </span>
                    </div>
                  ))}
                  {/* Display Videos */}
                  {videos.map((video, index) => (
                    <div key={`vid-${index}`} style={styles.miniFilePreviewItem}>
                      <div style={{ ...styles.miniMediaTypeBadge, backgroundColor: '#17a2b8' }}>VID</div>
                      <video
                        src={URL.createObjectURL(video)}
                        style={styles.miniPreviewThumbnail}
                        controls={false}
                        onClick={() => {
                          setSelectedMedia({
                            type: 'video',
                            url: URL.createObjectURL(video),
                            name: video.name
                          });
                          setShowMediaPopup(true);
                        }}
                      />
                      <span style={styles.miniFileName}>{video.name}</span>
                      <span
                        onClick={() => removeVideo(index)}
                        style={styles.miniRemoveButton}
                        title="Remove"
                      >
                        ×
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Products Table */}
        <div style={styles.tableContainer}>
          {/* Search and Filter Section */}
          <div style={{
            display: 'flex',
            gap: '80px',
            marginBottom: '15px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: '1 1 300px', minWidth: '250px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '5px', 
                fontSize: '13px', 
                fontWeight: '600',
                color: '#495057'
              }}>
                Search Product:
              </label>
              <input
                type="text"
                placeholder="Search by product name or ID..."
                value={searchProductName}
                onChange={(e) => setSearchProductName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '13px',
                  border: '1px solid #ced4da',
                  borderRadius: '5px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => e.target.style.borderColor = '#532a51ff'}
                onBlur={(e) => e.target.style.borderColor = '#ced4da'}
              />
            </div>

            <div style={{ flex: '0 0 250px', minWidth: '200px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '5px', 
                fontSize: '13px', 
                fontWeight: '600',
                color: '#495057'
              }}>
                Filter by Category:
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '13px',
                  border: '1px solid #ced4da',
                  borderRadius: '5px',
                  outline: 'none',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                }}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {(searchProductName || filterCategory) && (
              <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                <button
                  onClick={() => {
                    setSearchProductName("");
                    setFilterCategory("");
                  }}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                >
                  Clear Filters
                </button>
              </div>
            )}

            <div style={{ flex: '0 0 auto', alignSelf: 'flex-end', marginLeft: 'auto' }}>
              <div style={{ 
                fontSize: '13px', 
                color: '#6c757d',
                fontWeight: '500'
              }}>
                Showing {filteredProducts.length} of {products.length} products
              </div>
            </div>
          </div>

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>S.No</th>
                <th style={styles.th}>Product No</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Product Name</th>
                <th style={styles.th}>Units</th>
                <th style={styles.th}>Pieces</th>
                <th style={styles.th}>Price (₹)</th>
                <th style={styles.th}>Discount (%)</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Image</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" style={styles.loadingRow}>
                    Loading products...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="11" style={styles.errorRow}>
                    {error}
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan="11" style={styles.noDataRow}>
                    No products found
                  </td>
                </tr>
              ) : filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="11" style={styles.noDataRow}>
                    No products match your search criteria
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product, index) => (
                  <tr key={product.productId}>
                    <td style={styles.tdCenter}>{index + 1}</td>
                    <td style={styles.tdCenter}>{product.productId}</td>
                    <td style={styles.td}>{product.categoryName}</td>
                    <td style={styles.td}>{product.prodName}</td>
                    <td style={styles.td}>{product.unitName}</td>
                     <td style={styles.tdRight}>
  {product.pieces > 0 ? `${product.pieces} Pieces` : '-'}
</td>                
                    <td style={styles.tdRight}>{product.prodPrice?.toFixed(2) || "0.00"}</td>
                    <td style={styles.tdRight}>{product.discountPercentage || "0"}%</td>
                    <td style={styles.tdCenter}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        backgroundColor: product.productStatus === 'Hold' ? '#ffc107' : '#28a745',
                        color: 'white',
                      }}>
                        {product.productStatus || 'Visible'}
                      </span>
                    </td>
                    <td style={styles.tdCenter}>
                      {product.prodImage?.trim() ? (
                        (() => {
                          // Split comma-separated URLs
                          const mediaUrls = product.prodImage.split(',').map(url => url.trim()).filter(url => url);
                          const fullUrls = mediaUrls.map(url => url.startsWith("http") ? url : `${API_BASE_URL}${url}`);
                          
                          if (mediaUrls.length === 1) {
                            // Single image - display normally
                            const url = fullUrls[0];
                            const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(url);
                            
                            return isVideo ? (
                              <video
                                src={url}
                                style={{ ...styles.productImage, cursor: 'pointer' }}
                                controls={false}
                                title="Click to view"
                                onClick={() => {
                                  setProductMediaUrls(fullUrls);
                                  setCurrentMediaIndex(0);
                                  setShowProductMediaGallery(true);
                                }}
                              />
                            ) : (
                              <img
                                src={url}
                                alt="Product"
                                style={{ ...styles.productImage, cursor: 'pointer' }}
                                title="Click to view"
                                onClick={() => {
                                  setProductMediaUrls(fullUrls);
                                  setCurrentMediaIndex(0);
                                  setShowProductMediaGallery(true);
                                }}
                              />
                            );
                          } else {
                            // Multiple images - show grid with count badge
                            const firstUrl = fullUrls[0];
                            const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(firstUrl);
                            
                            return (
                              <div 
                                style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
                                onClick={() => {
                                  setProductMediaUrls(fullUrls);
                                  setCurrentMediaIndex(0);
                                  setShowProductMediaGallery(true);
                                }}
                                title="Click to view all"
                              >
                                {isVideo ? (
                                  <video
                                    src={firstUrl}
                                    style={styles.productImage}
                                    controls={false}
                                  />
                                ) : (
                                  <img
                                    src={firstUrl}
                                    alt="Product"
                                    style={styles.productImage}
                                  />
                                )}
                                <div style={{
                                  position: 'absolute',
                                  bottom: '2px',
                                  right: '2px',
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  color: 'white',
                                  borderRadius: '3px',
                                  padding: '2px 5px',
                                  fontSize: '9px',
                                  fontWeight: 'bold',
                                  border: '1px solid white',
                                }}>
                                  +{mediaUrls.length - 1}
                                </div>
                              </div>
                            );
                          }
                        })()
                      ) : (
                        <span style={styles.noImage}>No Image</span>
                      )}
                    </td>
                    <td style={styles.tdCenter}>
                      <div style={styles.actionContainer}>
                        <button
                          onClick={() => handleEdit(product.id)}
                          style={styles.editButton}
                          title="Edit"
                          disabled={isLoading}
                        >
                          <FiEdit size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          style={styles.deleteButton}
                          title="Delete"
                        >
                          <FiTrash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// Styles (same as before)
const styles = {
  container: {
    padding: "10px",
    maxHeight: "400px",
  },
  tdRight: {
    textAlign: "right",
    padding: "6px",
    border: "1px solid #ccc",
    fontSize: "12px",
  },
  titleContainer: {
    padding: "2px",
    borderRadius: "4px",
    textAlign: "left",
    marginBottom: "10px",
  },
  filterContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "15px",
    backgroundColor: "#f8f9fa",
    padding: "15px",
    borderRadius: "8px",
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    flex: "1 1 200px",
    minWidth: "200px",
  },
  filterLabel: {
    fontWeight: "bold",
    fontSize: "12px",
    width: "120px",
    color: "#333",
    flexShrink: 0,
  },
  input: {
    flex: "1",
    padding: "6px",
    borderRadius: "5px",
    border: "1px solid #000",
    fontSize: "12px",
    height: "32px",
    boxSizing: "border-box",
    minWidth: "150px",
  },
  select: {
    flex: "1",
    padding: "6px",
    borderRadius: "5px",
    border: "1px solid #000",
    fontSize: "12px",
    height: "32px",
    boxSizing: "border-box",
    backgroundColor: "white",
    minWidth: "150px",
  },
  addButton: {
    backgroundColor: "#532a51ff",
    border: "none",
    color: "white",
    cursor: "pointer",
    padding: "8px",
    width: "auto",
    minWidth: "100px",
    fontWeight: "bold",
    fontSize: "12px",
    alignSelf: "right",
    borderRadius: "4px",
    fontFamily: "inherit",
  },
  tableContainer: {
    maxHeight: "300px",
    overflowY: "auto",
    marginBottom: "40px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    backgroundColor: "#532a51ff",
    color: "#fff",
    textAlign: "center",
    fontSize: "12px",
    padding: "8px",
    position: "sticky",
    top: 0,
    zIndex: "1",
  },
  td: {
    padding: "6px",
    border: "1px solid #ccc",
    textAlign: "left",
    fontSize: "12px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tdCenter: {
    textAlign: "center",
    padding: "6px",
    border: "1px solid #ccc",
    fontSize: "12px",
  },
  productImage: {
    width: "30px",
    height: "30px",
    objectFit: "cover",
    borderRadius: "4px",
    border: "1px solid #ddd",
    backgroundColor: "#f0f0f0",
  },
  noImage: {
    fontSize: "12px",
    color: "#999",
  },
  actionContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
  },
  editButton: {
    padding: "6px",
    fontSize: "12px",
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    backgroundColor: "#bbbdbeff",
    color: "white",
  },
  deleteButton: {
    padding: "6px",
    fontSize: "12px",
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    backgroundColor: "#dc3545",
    color: "white",
  },
  noDataRow: {
    textAlign: "center",
    color: "#666",
    fontStyle: "italic",
    padding: "1rem",
  },
  loadingRow: {
    textAlign: "center",
    padding: "1rem",
  },
  errorRow: {
    textAlign: "center",
    color: "#dc3545",
    padding: "1rem",
  },
  fileInputDisplay: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: "8px",
    flex: 1,
    padding: "6px",
    borderRadius: "5px",
    border: "1px solid #000",
    fontSize: "12px",
    height: "32px",
    boxSizing: "border-box",
    backgroundColor: "white",
  },
  imageLink: {
    fontSize: "13px",
    color: "#0066cc",
    textDecoration: "underline",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  removeImage: {
    fontSize: "16px",
    color: "#dc3545",
    fontWeight: "bold",
    cursor: "pointer",
    width: "16px",
    height: "16px",
    textAlign: "center",
    border: "1px solid #dc3545",
    borderRadius: "50%",
    lineHeight: "14px",
  },
  browseButton: {
    cursor: "pointer",
    padding: "4px 8px",
    backgroundColor: "#e9ecef",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "13px",
  },
  compactFilePreviewContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '4px',
  },
  miniFilePreviewItem: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '4px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: '#f8f9fa',
    width: '60px',
  },
  miniPreviewThumbnail: {
    width: '50px',
    height: '50px',
    objectFit: 'cover',
    borderRadius: '3px',
    marginBottom: '2px',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  },
  miniFileName: {
    fontSize: '8px',
    color: '#666',
    textAlign: 'center',
    wordBreak: 'break-word',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
  miniRemoveButton: {
    position: 'absolute',
    top: '1px',
    right: '1px',
    fontSize: '12px',
    color: '#dc3545',
    fontWeight: 'bold',
    cursor: 'pointer',
    width: '14px',
    height: '14px',
    textAlign: 'center',
    backgroundColor: 'white',
    border: '1px solid #dc3545',
    borderRadius: '50%',
    lineHeight: '12px',
  },
  miniMediaTypeBadge: {
    position: 'absolute',
    top: '1px',
    left: '1px',
    fontSize: '7px',
    padding: '1px 3px',
    backgroundColor: '#28a745',
    color: 'white',
    borderRadius: '2px',
    fontWeight: 'bold',
    zIndex: 1,
  },
};

export default AdminProduct;